<template>
  <div v-if="showAlerts" class="mb-5">
    <v-card color="alert" class="pa-4 mb-5">
      <v-row dense>
        <v-col
          cols="12"
          sm="1"
          class="px-4 d-flex justify-center"
          :class="{
            'mb-3': $vuetify.display.xs
          }"
          style="align-items: baseline"
        >
          <v-icon size="large" color="red"> $iconBell </v-icon>
        </v-col>
        <v-col>
          <component :is="multipleAlerts ? 'ul' : 'div'">
            <template v-for="(alert, index) in eventLimitAlerts" :key="index">
              <component :is="multipleAlerts ? 'li' : 'div'" class="mb-1">
                {{ alert }}
              </component>
            </template>
          </component>
        </v-col>
      </v-row>
    </v-card>
    <slot name="additionals" />
  </div>
</template>
<script>
import gql from "fraql";
import { useCompetitionHelpers } from "@composables/competition_helpers";
export default {
  name: "EventRegistrationsLimited",
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  setup() {
    const compHelpers = useCompetitionHelpers();
    return { ...compHelpers };
  },
  computed: {
    showNormalAlerts() {
      return ["published", "schedule_proposed", "registration_open"].includes(
        this.event.status
      );
    },
    showMaxHorsesPerAthleteAlert() {
      return [
        "published",
        "schedule_proposed",
        "registration_open",
        "registration_closed",
        "event_running",
        "event_ended"
      ].includes(this.event.status);
    },
    showMaxNumberOfEntriesPerHorse() {
      return [
        "published",
        "schedule_proposed",
        "registration_open",
        "registration_closed",
        "event_running",
        "event_ended"
      ].includes(this.event.status);
    },
    showAlerts() {
      return this.eventLimitAlerts.length;
    },
    multipleAlerts() {
      return this.eventLimitAlerts.length > 1;
    },
    eventLimitAlerts() {
      const alerts = [];
      const wholeEvent = this.event.eventLimits.find(
        x => x.limitScope == "WHOLE_EVENT"
      );

      const competitionLimits = this.event.eventLimits.filter(
        x => x.limitScope == "COMPETITIONS"
      );

      if (wholeEvent) {
        if (this.showNormalAlerts && wholeEvent.maxNumberOfEntries) {
          let alertEntriesText = null;
          const numberOfAvailableSlotsForEntries =
            (wholeEvent.maxNumberOfEntries || 0) -
            (wholeEvent.entriesRegistrationConfirmedCount || 0);

          if (numberOfAvailableSlotsForEntries > 10) {
            alertEntriesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.wholeEventMaxNumberOfEntries.limitSet.text",
                `Please note, maximum number of entries for the whole event is set. Organizer accepts {{maxNumberOfEntries}} entries. Number of free places is:  {{numberOfAvailableSlotsForEntries}}.`
              ],
              {
                maxNumberOfEntries: wholeEvent.maxNumberOfEntries,
                numberOfAvailableSlotsForEntries
              }
            );
          } else if (
            numberOfAvailableSlotsForEntries > 0 &&
            numberOfAvailableSlotsForEntries <= 10
          ) {
            alertEntriesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.wholeEventMaxNumberOfEntries.lessThanTenPlacesLeft.text",
                `Please note, maximum number of entries for the whole event is set. Organizer accepts {{maxNumberOfEntries}} entries. Event is very popular. Only {{numberOfAvailableSlotsForEntries}} free places left. Check registration limits. `
              ],
              {
                maxNumberOfEntries: wholeEvent.maxNumberOfEntries,
                numberOfAvailableSlotsForEntries
              }
            );
          } else {
            alertEntriesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.wholeEventMaxNumberOfEntries.limitReached.text",
                `Organizer accepts {{maxNumberOfEntries}} entries. No free places left. Event doesn't accept new registrations.`
              ],
              {
                maxNumberOfEntries: wholeEvent.maxNumberOfEntries
              }
            );
          }

          alerts.push(alertEntriesText);
        }

        if (this.showNormalAlerts && wholeEvent.maxNumberOfAthletes) {
          let alertAthletesText = null;
          const numberOfAvailableSlotsForAthletes =
            (wholeEvent.maxNumberOfAthletes || 0) -
            (wholeEvent.athletesRegistrationConfirmedCount || 0);

          if (numberOfAvailableSlotsForAthletes > 10) {
            alertAthletesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.wholeEventMaxNumberOfAthletes.limitSet.text",
                `Please note, maximum number of unique athletes for the whole event is set. Organizer accepts {{maxNumberOfAthletes}} participants. Number of free places is:  {{numberOfAvailableSlotsForAthletes}}.`
              ],
              {
                maxNumberOfAthletes: wholeEvent.maxNumberOfAthletes,
                numberOfAvailableSlotsForAthletes
              }
            );
          } else if (
            numberOfAvailableSlotsForAthletes > 0 &&
            numberOfAvailableSlotsForAthletes <= 10
          ) {
            alertAthletesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.wholeEventMaxNumberOfAthletes.lessThanTenPlacesLeft.text",
                `Please note, maximum number of unique athletes for the whole event is set. Organizer accepts {{maxNumberOfAthletes}} participants. Event is very popular. Only {{numberOfAvailableSlotsForAthletes}} more athletes can register.`
              ],
              {
                maxNumberOfAthletes: wholeEvent.maxNumberOfAthletes,
                numberOfAvailableSlotsForAthletes
              }
            );
          } else {
            alertAthletesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.wholeEventMaxNumberOfAthletes.limitReached.text",
                `Organizer accepts {{maxNumberOfAthletes}} participants. No free places left. Event doesn't accept new registrations.`
              ],
              {
                maxNumberOfAthletes: wholeEvent.maxNumberOfAthletes
              }
            );
          }

          alerts.push(alertAthletesText);
        }

        if (
          this.showMaxHorsesPerAthleteAlert &&
          wholeEvent.maxNumberOfHorsesPerAthlete
        ) {
          alerts.push(
            this.$t(
              [
                "events:EventPage.EventLimitsNotification.wholeEventMaxNumberOfHorsesPerAthlete.limitSet.text",
                "Please note, maximum number of horses per athlete for the whole event is set. Organizer accepts {{maxNumberOfHorsesPerAthlete}} horses per athlete. "
              ],
              {
                maxNumberOfHorsesPerAthlete:
                  wholeEvent.maxNumberOfHorsesPerAthlete
              }
            )
          );
        }

        if (
          this.showMaxNumberOfEntriesPerHorse &&
          wholeEvent.maxNumberOfEntriesPerHorse
        ) {
          alerts.push(
            this.$t(
              [
                "events:EventPage.EventLimitsNotification.wholeEventMaxNumberOfEntriesPerHorse.limitSet.text",
                "Please note, maximum number of entries a horse can do over the whole event is {{maxNumberOfEntriesPerHorse}}."
              ],
              {
                maxNumberOfEntriesPerHorse:
                  wholeEvent.maxNumberOfEntriesPerHorse
              }
            )
          );
        }
      }

      competitionLimits.forEach(competitionLimit => {
        const sortFn = this.competitionOrderFn;
        const competitionCodes = competitionLimit.competitionIds
          .map(compId => {
            const comp = this.event.competitions.find(c => c.id == compId);
            return comp.code;
          })
          .sort(sortFn)
          .join(" + ");

        if (this.showNormalAlerts && competitionLimit.maxNumberOfEntries) {
          let alertEntriesText = null;

          const numberOfAvailableSlotsForEntries =
            (competitionLimit.maxNumberOfEntries || 0) -
            (competitionLimit.entriesRegistrationConfirmedCount || 0);

          if (numberOfAvailableSlotsForEntries > 10) {
            alertEntriesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.competitionsGroupMaxNumberOfEntries.limitSet.text",
                `Please note that the maximum number of entries over competitions {{competitionCodes}} is limited to {{maxNumberOfEntries}}. Number of free places is: {{numberOfAvailableSlotsForEntries}}.`
              ],
              {
                maxNumberOfEntries: competitionLimit.maxNumberOfEntries,
                numberOfAvailableSlotsForEntries,
                competitionCodes
              }
            );
          } else if (
            numberOfAvailableSlotsForEntries > 0 &&
            numberOfAvailableSlotsForEntries <= 10
          ) {
            alertEntriesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.competitionsGroupMaxNumberOfEntries.lessThanTenPlacesLeft.text",
                `Please note that the maximum number of entries over competitions {{competitionCodes}} is limited to {{maxNumberOfEntries}}. Number of free places is: {{numberOfAvailableSlotsForEntries}}.`
              ],
              {
                maxNumberOfEntries: competitionLimit.maxNumberOfEntries,
                numberOfAvailableSlotsForEntries,
                competitionCodes
              }
            );
          } else {
            alertEntriesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.competitionsGroupMaxNumberOfEntries.limitReached.text",
                `Please note that the maximum number of entries over competitions {{competitionCodes}} is limited to {{maxNumberOfEntries}}. No free places are left and we cannot accept new registrations for these competitions.`
              ],
              {
                maxNumberOfEntries: competitionLimit.maxNumberOfEntries,
                competitionCodes
              }
            );
          }

          alerts.push(alertEntriesText);
        }

        if (this.showNormalAlerts && competitionLimit.maxNumberOfAthletes) {
          let alertAthletesText = null;
          const numberOfAvailableSlotsForAthletes =
            (competitionLimit.maxNumberOfAthletes || 0) -
            (competitionLimit.athletesRegistrationConfirmedCount ?? 0);
          if (numberOfAvailableSlotsForAthletes > 10) {
            alertAthletesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.competitionsGroupMaxNumberOfAthletes.limitSet.text",
                `Please note that the maximum number of athletes over competitions {{competitionCodes}} is limited to {{maxNumberOfAthletes}}. Number of free places is: {{numberOfAvailableSlotsForAthletes}}.`
              ],
              {
                maxNumberOfAthletes: competitionLimit.maxNumberOfAthletes,
                numberOfAvailableSlotsForAthletes,
                competitionCodes
              }
            );
          } else if (
            numberOfAvailableSlotsForAthletes > 0 &&
            numberOfAvailableSlotsForAthletes <= 10
          ) {
            alertAthletesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.competitionsGroupMaxNumberOfAthletes.lessThanTenPlacesLeft.text",
                `Please note that the maximum number of athletes over competitions {{competitionCodes}} is limited to {{maxNumberOfAthletes}}. Number of free places is: {{numberOfAvailableSlotsForAthletes}}.`
              ],
              {
                maxNumberOfAthletes: competitionLimit.maxNumberOfAthletes,
                numberOfAvailableSlotsForAthletes,
                competitionCodes
              }
            );
          } else {
            alertAthletesText = this.$t(
              [
                "events:EventPage.EventLimitsNotification.competitionsGroupMaxNumberOfAthletes.limitReached.text",
                `Please note that the maximum number of athletes over competitions {{competitionCodes}} is limited to {{maxNumberOfAthletes}}. No free places are left and we cannot accept new athletes for these competitions.`
              ],
              {
                maxNumberOfAthletes: competitionLimit.maxNumberOfAthletes,
                competitionCodes
              }
            );
          }
          alerts.push(alertAthletesText);
        }

        if (
          this.showMaxHorsesPerAthleteAlert &&
          competitionLimit.maxNumberOfHorsesPerAthlete
        ) {
          alerts.push(
            this.$t(
              [
                "events:EventPage.EventLimitsNotification.competitionsGroupMaxNumberOfHorsesPerAthlete.limitSet.text",
                "Please note, maximum number of horses per athlete over competitions {{competitionCodes}} is set. Organizer accepts {{maxNumberOfHorsesPerAthlete}} horses per athlete."
              ],
              {
                maxNumberOfHorsesPerAthlete:
                  competitionLimit.maxNumberOfHorsesPerAthlete,
                competitionCodes
              }
            )
          );
        }

        if (
          this.showMaxNumberOfEntriesPerHorse &&
          competitionLimit.maxNumberOfEntriesPerHorse
        ) {
          alerts.push(
            this.$t(
              [
                "events:EventPage.EventLimitsNotification.competitionsGroupMaxNumberOfEntriesPerHorse.limitSet.text",
                "Please note, maximum number of entries a horse can do over competitions {{competitionCodes}} is set. Horse can only start in maximum {{maxNumberOfEntriesPerHorse}} competitions. "
              ],
              {
                maxNumberOfEntriesPerHorse:
                  competitionLimit.maxNumberOfEntriesPerHorse,
                competitionCodes
              }
            )
          );
        }
      });

      return alerts;
    }
  },
  fragments: {
    eventLimitFields: gql`
      fragment _ on EventLimit {
        id
        limitScope

        maxNumberOfEntries
        entriesRegistrationConfirmedCount

        maxNumberOfAthletes
        athletesRegistrationConfirmedCount

        maxNumberOfHorsesPerAthlete
        maxNumberOfEntriesPerHorse
        competitionIds
      }
    `
  }
};
</script>
